import { useEffect, useState } from 'react'
import { AppDialog, Button, MiniLoader, StatusChip, Textarea, ActionMenu } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import type { EduAttendanceStatus } from '../../types/schema'
import {
  getEduSessionRoll,
  markEduAttendance,
  type RollLearner,
} from '../../services/eduAttendanceService'
import { nextStatus } from '../../utils/eduAttendance'
import { mapEduServerError } from './classesBuilderHelpers'
import './eduRegisterDialog.css'

export interface EduRegisterDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  seriesId: string
  /** ISO date string YYYY-MM-DD */
  date: string
  /** Localised session label, e.g. "Maths 10 A — 09:00" */
  sessionLabel: string
  /** Whether today is strictly the session date (workspace-tz). False → reason required. */
  isToday: boolean
  /** Optional callback after attendance is saved successfully */
  onSaved?: () => void
  /** Whether this session is open for booking (EQ167) */
  isBookable?: boolean
}

export const EduRegisterDialog = ({
  isOpen,
  onClose,
  workspaceId,
  seriesId,
  date,
  sessionLabel,
  isToday,
  onSaved,
  isBookable = false,
}: EduRegisterDialogProps) => {
  const { t } = useNls()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [roll, setRoll] = useState<RollLearner[]>([])
  const [marks, setMarks] = useState<Record<string, EduAttendanceStatus>>({})
  const [onLeaveIds, setOnLeaveIds] = useState<Set<string>>(new Set())
  const [reason, setReason] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const requiresReason = !isToday

  const handleClose = () => {
    setRoll([])
    setMarks({})
    setOnLeaveIds(new Set())
    setReason('')
    setSaveError(null)
    setError(null)
    setLoading(true)
    onClose()
  }

  // Load roll whenever dialog opens without synchronous setState in effect body.
  useEffect(() => {
    if (!isOpen) return
    let active = true

    const load = async () => {
      try {
        const result = await getEduSessionRoll({ workspaceId, seriesId, date })
        if (!active) return
        setRoll(result.learners)
        const onLeaveSet = new Set(result.onLeaveLearnerIds ?? [])
        setOnLeaveIds(onLeaveSet)
        // Existing marks from server; on-leave learners start as ON_LEAVE; un-marked learners default to PRESENT (EQ54)
        const initialMarks: Record<string, EduAttendanceStatus> = {}
        for (const learner of result.learners) {
          if (onLeaveSet.has(learner.learnerId)) {
            initialMarks[learner.learnerId] = 'ON_LEAVE'
          } else {
            initialMarks[learner.learnerId] =
              (result.marks[learner.learnerId] as EduAttendanceStatus | undefined) ?? 'PRESENT'
          }
        }
        setMarks(initialMarks)
        setLoading(false)
      } catch (err: unknown) {
        if (!active) return
        setError(mapEduServerError(err, t))
        setLoading(false)
      }
    }

    void load()

    return () => {
      active = false
    }
  }, [isOpen, workspaceId, seriesId, date, t])

  const handleCycle = (learnerId: string) => {
    if (onLeaveIds.has(learnerId)) return
    setMarks((prev) => ({
      ...prev,
      [learnerId]: nextStatus(prev[learnerId] ?? 'PRESENT'),
    }))
  }

  const handleSave = async () => {
    setSaveError(null)
    setIsSaving(true)
    try {
      const markItems = roll
        .filter((l) => !l.frozen)
        .map((l) => ({
          learnerId: l.learnerId,
          status: onLeaveIds.has(l.learnerId) ? 'ON_LEAVE' : (marks[l.learnerId] ?? 'PRESENT'),
        }))
      await markEduAttendance({
        workspaceId,
        seriesId,
        date,
        marks: markItems,
        ...(requiresReason && reason.trim() ? { reason: reason.trim() } : {}),
      })
      onSaved?.()
      handleClose()
    } catch (err: unknown) {
      setSaveError(mapEduServerError(err, t))
    } finally {
      setIsSaving(false)
    }
  }


  // Live counts
  const counts = roll.reduce(
    (acc, l) => {
      const s = onLeaveIds.has(l.learnerId) ? 'ON_LEAVE' : (marks[l.learnerId] ?? 'PRESENT')
      if (s === 'PRESENT') acc.present++
      else if (s === 'ABSENT') acc.absent++
      else if (s === 'LATE') acc.late++
      return acc
    },
    { present: 0, absent: 0, late: 0 },
  )

  const statusLabel = (status: EduAttendanceStatus): string => {
    switch (status) {
      case 'PRESENT':
        return t('NLSEducationAttendanceStatusPresent')
      case 'ABSENT':
        return t('NLSEducationAttendanceStatusAbsent')
      case 'LATE':
        return t('NLSEducationAttendanceStatusLate')
      case 'ON_LEAVE':
        return t('NLSEducationAttendanceStatusOnLeave')
      case 'EXCUSED':
        return t('NLSEducationAttendanceStatusExcused')
    }
  }

  const statusTone = (
    status: EduAttendanceStatus,
  ): 'success' | 'warning' | 'danger' | 'neutral' => {
    switch (status) {
      case 'PRESENT':
        return 'success'
      case 'LATE':
        return 'warning'
      case 'ABSENT':
        return 'danger'
      default:
        return 'neutral'
    }
  }

  const saveDisabled =
    isSaving ||
    loading ||
    Boolean(error) ||
    (requiresReason && reason.trim().length < 3)

  return (
    <AppDialog
      open={isOpen}
      onDismiss={handleClose}
      title={t('NLSEducationRegisterTitle')}
      size="md"
      actions={[]}
    >
      <div className="eduRegister" data-testid="edu-register-dialog">
        {/* Session meta */}
        <div className="eduRegister__meta">
          <span className="eduRegister__metaItem">
            <span className="eduRegister__metaLabel">{t('NLSEducationRegisterSession')}</span>
            <span className="eduRegister__metaValue">{sessionLabel}</span>
          </span>
          <span className="eduRegister__metaItem">
            <span className="eduRegister__metaLabel">{t('NLSEducationRegisterDate')}</span>
            <span className="eduRegister__metaValue">{date}</span>
          </span>
        </div>

        {/* Booking hint (EQ167) */}
        {isBookable && (
          <div className="eduRegister__bookingHint" data-testid="register-booking-hint">
            {t('NLSEducationRegisterBookingHint')}
          </div>
        )}

        {/* Errors */}
        {(error || saveError) && (
          <div className="eduRegister__error" role="alert" data-testid="register-error">
            {error ?? saveError}
          </div>
        )}

        {loading ? (
          <div className="eduRegister__loading">
            <MiniLoader />
            <span>{t('NLSEducationRegisterLoading')}</span>
          </div>
        ) : roll.length === 0 ? (
          <p className="eduRegister__empty">{t('NLSEducationRegisterEmpty')}</p>
        ) : (
          <>
            {/* Counts */}
            <div className="eduRegister__counts" data-testid="register-counts">
              <span>{t('NLSEducationRegisterSummaryPresent', { count: counts.present })}</span>
              <span>{t('NLSEducationRegisterSummaryAbsent', { count: counts.absent })}</span>
              <span>{t('NLSEducationRegisterSummaryLate', { count: counts.late })}</span>
            </div>

            {/* Roll table */}
            <div className="eduRegister__tableWrapper">
              <table className="eduRegister__table">
                <thead>
                  <tr>
                    <th>{t('NLSEducationRegisterColLearner')}</th>
                    <th>{t('NLSEducationRegisterColRoll')}</th>
                    <th>{t('NLSEducationRegisterColStatus')}</th>
                    <th aria-label={t('NLSEducationRegisterColStatus')} />
                  </tr>
                </thead>
                <tbody>
                  {roll.map((learner) => {
                    const isOnLeave = onLeaveIds.has(learner.learnerId)
                    const status: EduAttendanceStatus = isOnLeave
                      ? 'ON_LEAVE'
                      : (marks[learner.learnerId] ?? 'PRESENT')
                    const frozen = learner.frozen
                    const isFixed = status === 'ON_LEAVE' || status === 'EXCUSED'
                    const canCycle = !frozen && !isFixed && !isOnLeave

                    const handleSetFixed = (next: EduAttendanceStatus) => {
                      if (isOnLeave) return
                      setMarks((prev) => ({ ...prev, [learner.learnerId]: next }))
                    }

                    const menuItems = (frozen || isOnLeave)
                      ? []
                      : isFixed
                        ? [
                            {
                              key: 'reset',
                              label: t('NLSEducationRegisterMenuResetPresent'),
                              onSelect: () => handleSetFixed('PRESENT'),
                            },
                          ]
                        : [
                            {
                              key: 'on-leave',
                              label: t('NLSEducationRegisterMenuSetOnLeave'),
                              onSelect: () => handleSetFixed('ON_LEAVE'),
                            },
                            {
                              key: 'excused',
                              label: t('NLSEducationRegisterMenuSetExcused'),
                              onSelect: () => handleSetFixed('EXCUSED'),
                            },
                          ]

                    return (
                      <tr key={learner.learnerId} data-testid={`register-row-${learner.learnerId}`}>
                        <td>
                          <span className="eduRegister__learnerName">{learner.displayName}</span>
                          {frozen && (
                            <span className="eduRegister__frozenNote" role="note">
                              {t('NLSEducationRegisterFrozenNote')}
                            </span>
                          )}
                          {isOnLeave && (
                            <span className="eduRegister__frozenNote" role="note">
                              {t('NLSEducationRegisterOnLeaveNote')}
                            </span>
                          )}
                        </td>
                        <td className="eduRegister__rollNo">
                          {learner.rollNo ?? '—'}
                        </td>
                        <td>
                          <Button
                            variant="tertiary"
                            size="sm"
                            className={`eduRegister__statusBtn eduRegister__statusBtn--${status.toLowerCase()}`}
                            disabled={!canCycle}
                            aria-label={`${learner.displayName}: ${statusLabel(status)}`}
                            data-testid={`status-btn-${learner.learnerId}`}
                            onClick={() => handleCycle(learner.learnerId)}
                          >
                            <StatusChip tone={statusTone(status)}>
                              {statusLabel(status)}
                            </StatusChip>
                          </Button>
                        </td>
                        <td>
                          {menuItems.length > 0 && (
                            <ActionMenu
                              trigger="kebab"
                              ariaLabel={t('NLSEducationRegisterLearnerOptions', {
                                name: learner.displayName,
                              })}
                              items={menuItems}
                              size="sm"
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

            </div>

            {/* Reason for past sessions */}
            {requiresReason && (
              <div className="eduRegister__reasonSection">
                <label className="eduRegister__reasonLabel" htmlFor="register-reason">
                  {t('NLSEducationRegisterReasonLabel')}
                </label>
                <Textarea
                  id="register-reason"
                  value={reason}
                  onChange={(v) => setReason(v.slice(0, 500))}
                  placeholder={t('NLSEducationRegisterReasonPlaceholder')}
                  maxLength={500}
                  aria-label={t('NLSEducationRegisterReasonLabel')}
                  data-testid="register-reason-input"
                />
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="eduRegister__actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            data-testid="register-close-btn"
          >
            {t('NLSEducationRegisterClose')}
          </Button>
          {!loading && !error && roll.length > 0 && (
            <Button
              variant="primary"
              size="sm"
              disabled={saveDisabled}
              onClick={() => void handleSave()}
              data-testid="register-save-btn"
            >
              {isSaving ? t('NLSEducationRegisterSaving') : t('NLSEducationRegisterSave')}
            </Button>
          )}
        </div>
      </div>
    </AppDialog>
  )
}
